<script setup lang="ts">
import type { NavigationMenuItem } from '@nuxt/ui'
import { nextTick } from 'vue'
import {
  CONTENT_DATA_QUERY_KEYS,
  POLICY_QUERY_KEYS,
} from '~/queries/paapi/content-data'

const { recents, remove: removeRecent } = useRecentPages()
const {
  defaultItems,
  engineeringItems,
  teamItems,
  toolsItems,
} = useTeamNav()

/*
 * Nuxt UI keys each "Recent" child by array index, so removing a non-last
 * entry lets Vue's keyed diff reuse the clicked link's DOM node for the
 * next item (swapping its href) before the browser's own click activation
 * for that node runs. Deferring the mutation lets that activation (already
 * canceled below via .prevent) fully settle before indices shift.
 */
const onRemoveRecent = (path: string) => {
  nextTick(() => removeRecent(path))
}

const { isFetching: policiesFetching } = useQueryStateExtended(
  POLICY_QUERY_KEYS.root,
)
const { isFetching: refDocsFetching } = useQueryStateExtended(
  CONTENT_DATA_QUERY_KEYS.referenceDocuments,
)
const pendingIcon = {
  trailingIcon: 'i-svg-spinners-3-dots-fade',
}

const { data: docsItems } = useAsyncData(
  'navigation-menu/docs/all.json',
  async () => {
    const result = await queryCollectionNavigation('docs')
    return result.map((item) => ({
      label: item.title,
      to: `/docs${item.path}`,
    }))
  },
)
/* Separate destructure: inserting inside the block above would put the hunk's
   context on lines owned by the policy-initiatives branch. */
const { policyStudioItems } = useTeamNav()

const items = computed<NavigationMenuItem[][]>(() => [
  [
    {
      label: 'Home',
      icon: 'i-ph-house',
      to: '/dashboard',
    },
  ],
  ...(teamItems.value.length ? [teamItems.value] : []),
  ...(defaultItems.value.length ? [defaultItems.value] : []),
  [
    {
      label: 'Content Library',
      icon: 'i-ph-books',
      children: [
        {
          label: 'Policies',
          to: '/content-library/policies',
          ...(policiesFetching.value ? pendingIcon : {}),
        },
        {
          label: 'Sources',
          to: '/content-library/reference-documents',
          ...(refDocsFetching.value ? pendingIcon : {}),
        },
        {
          label: 'Engines',
          to: '/content-library/engines',
        },
        {
          label: 'Logic Types',
          to: '/content-library/logic-types',
        },
        {
          label: 'Ref data',
          to: '/content-library/ref-data/diagnosis-codes',
        },
        {
          label: 'Source Types',
          to: '/content-library/source-types/level-one',
        },
        {
          label: 'Topics',
          to: '/content-library/topics',
        },
      ],
    },
  ],
  ...(policyStudioItems.value.length ? [policyStudioItems.value] : []),
  ...(toolsItems.value.length
    ? [
        [
          {
            label: 'Tools',
            icon: 'i-ph-toolbox',
            children: toolsItems.value,
          },
        ],
      ]
    : []),
  [
    {
      label: 'Documentation',
      icon: 'i-ph-book-open-text',
      children: docsItems.value,
    },
  ],
  ...(engineeringItems.value ? [[engineeringItems.value]] : []),
  ...(recents.value.length
    ? [
        [
          {
            label: 'Recent',
            icon: 'i-ph-clock-counter-clockwise',
            children: recents.value.map((recent) => ({
              slot: 'recent',
              label: recent.label,
              to: recent.path,
            })),
          },
        ],
      ]
    : []),
])
</script>

<template>
  <UNavigationMenu
    :items="items"
    highlight
    highlight-color="primary"
    orientation="vertical"
    variant="pill"
  >
    <template #recent-trailing="{ item }: { item: NavigationMenuItem }">
      <UButton
        aria-label="Remove from recent"
        color="neutral"
        icon="i-ph-x"
        size="xs"
        variant="ghost"
        @click.stop.prevent="onRemoveRecent(item.to as string)"
      />
    </template>
  </UNavigationMenu>
</template>
